import type { ReactiveFormRequestHandler } from '../../adapters/reactive-form-builder.adapters';

export type ReactiveFormSubmitRequestStatus = 'syncing' | 'finish' | 'error';

export interface ReactiveFormSubmitRequestExecutorConfiguration<TPayload, TResponse> {
    readonly request: ReactiveFormRequestHandler<TResponse, TPayload>;
    readonly payload: TPayload;
    readonly onSuccess: (response: TResponse) => void;
    readonly onFailure: (error: unknown) => void;
    readonly refreshAfterSuccess?: () => void;
    readonly scheduleCleanup: (cleanup: () => void) => void;
}

/**
 * Executes legacy RequestHandler-style submit requests without owning the concrete app request class.
 */
export class ReactiveFormSubmitRequestExecutor<TPayload, TResponse> {
    public execute(configuration: ReactiveFormSubmitRequestExecutorConfiguration<TPayload, TResponse>): void {
        const { request, payload } = configuration;
        let requestFinished = false;

        request.fetchStatus?.((status: ReactiveFormSubmitRequestStatus) => {
            if (status !== 'error' || requestFinished) {
                return;
            }

            requestFinished = true;
            configuration.onFailure(request.error);
            configuration.scheduleCleanup(() => request.unsuscribe());
        });

        request.safePerform(payload);
        request.response((response) => {
            if (requestFinished) {
                return;
            }

            requestFinished = true;
            configuration.refreshAfterSuccess?.();
            configuration.onSuccess(response);
            configuration.scheduleCleanup(() => request.unsuscribe());
        });
    }
}
